/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Bookmark, Newspaper, Link as LinkIcon, Handshake, ExternalLink } from 'lucide-react';
import PostCard from '@/components/PostCard';
import BookmarkButton from '@/components/BookmarkButton';
import { timeAgo } from '@/lib/utils-cat';

const EmptyState = ({ Icon, title, subtitle }) => (
  <div className="text-center py-10 opacity-70">
    <div className="mx-auto h-12 w-12 rounded-2xl bg-secondary/60 flex items-center justify-center mb-3">
      <Icon className="h-5 w-5" />
    </div>
    <div className="font-semibold text-sm">{title}</div>
    <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
  </div>
);

const SavedPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/bookmarks');
      setItems(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const posts = items.filter(x => x.target_type === 'post');
  const resources = items.filter(x => x.target_type === 'resource');
  const initiatives = items.filter(x => x.target_type === 'initiative');

  const onUnsaved = (id) => setItems(prev => prev.filter(x => x.target?.id !== id));

  return (
    <div className="space-y-4 max-w-3xl mx-auto pb-8" data-testid="saved-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Bookmark className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display font-semibold text-2xl leading-tight tracking-tight">{t('savedTitle')}</h1>
          <p className="text-xs text-muted-foreground">{t('savedSubtitle')}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all" data-testid="saved-tab-all">{t('all')} ({items.length})</TabsTrigger>
          <TabsTrigger value="posts" data-testid="saved-tab-posts">{t('posts')} ({posts.length})</TabsTrigger>
          <TabsTrigger value="resources" data-testid="saved-tab-resources">{t('resources')} ({resources.length})</TabsTrigger>
          <TabsTrigger value="initiatives" data-testid="saved-tab-initiatives">{t('bacheca')} ({initiatives.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4 space-y-3">
          {loading && <div className="text-sm text-muted-foreground">{t('loading')}</div>}
          {!loading && items.length === 0 && (
            <EmptyState Icon={Bookmark} title={t('savedEmptyTitle')} subtitle={t('savedEmptySub')} />
          )}
          {items.map(bm => (
            <SavedRow key={bm.id} bm={bm} onUnsaved={onUnsaved} navigate={navigate} t={t} />
          ))}
        </TabsContent>

        <TabsContent value="posts" className="mt-4 space-y-3">
          {posts.length === 0 && <EmptyState Icon={Newspaper} title={t('savedEmptyPostsTitle')} subtitle={t('savedEmptyPostsSub')} />}
          {posts.map(bm => (
            <PostCard
              key={bm.id}
              post={{ ...bm.target, _bookmarked: true }}
              onChanged={() => {}}
              onDeleted={(pid) => onUnsaved(pid)}
            />
          ))}
        </TabsContent>

        <TabsContent value="resources" className="mt-4 space-y-3">
          {resources.length === 0 && <EmptyState Icon={LinkIcon} title={t('savedEmptyResourcesTitle')} subtitle={t('savedEmptyResourcesSub')} />}
          {resources.map(bm => (
            <SavedRow key={bm.id} bm={bm} onUnsaved={onUnsaved} navigate={navigate} t={t} />
          ))}
        </TabsContent>

        <TabsContent value="initiatives" className="mt-4 space-y-3">
          {initiatives.length === 0 && <EmptyState Icon={Handshake} title={t('savedEmptyInitiativesTitle')} subtitle={t('savedEmptyInitiativesSub')} />}
          {initiatives.map(bm => (
            <SavedRow key={bm.id} bm={bm} onUnsaved={onUnsaved} navigate={navigate} t={t} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Row renderer used in 'all' and simple lists (resource/initiative)
const SavedRow = ({ bm, onUnsaved, navigate, t }) => {
  const target = bm.target;
  if (!target) return null;
  const type = bm.target_type;

  if (type === 'post') {
    return (
      <PostCard post={{ ...target, _bookmarked: true }} onChanged={() => {}} onDeleted={(pid) => onUnsaved(pid)} />
    );
  }
  if (type === 'resource') {
    return (
      <Card className="p-4 rounded-2xl" data-testid={`saved-resource-${target.id}`}>
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <LinkIcon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <a href={target.url} target="_blank" rel="noreferrer" className="font-semibold text-sm hover:underline inline-flex items-center gap-1.5" data-testid={`saved-resource-link-${target.id}`}>
              {target.title}
              <ExternalLink className="h-3 w-3 flex-shrink-0" />
            </a>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{target.description}</p>
            <div className="text-[10px] text-muted-foreground mt-1.5">{timeAgo(bm.created_at, t)} · {target.type}</div>
          </div>
          <BookmarkButton
            targetType="resource"
            targetId={target.id}
            initialSaved
            onChange={(saved) => { if (!saved) onUnsaved(target.id); }}
          />
        </div>
      </Card>
    );
  }
  if (type === 'initiative') {
    return (
      <Card className="p-4 rounded-2xl cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => navigate('/bacheca')} data-testid={`saved-initiative-${target.id}`}>
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Handshake className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm truncate">{target.title}</div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{target.description}</p>
            <div className="text-[10px] text-muted-foreground mt-1.5">{timeAgo(bm.created_at, t)} · {target.type || 'initiative'}</div>
          </div>
          <BookmarkButton
            targetType="initiative"
            targetId={target.id}
            initialSaved
            onChange={(saved) => { if (!saved) onUnsaved(target.id); }}
          />
        </div>
      </Card>
    );
  }
  return null;
};

export default SavedPage;
